import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectAccessToken, selectPermissions } from '../../../slices/staffSlice';
import { show } from '../../../slices/toastSlice';
import { fetchRequests } from '../../../Requests/getRequests';
import { authStaff } from '../../../utils/constants';
import { Loader } from '../../../components';
import MasterInputSection from './MasterInputSection';
import DownloadTemplateButton from './DownloadTemplateButton';
import CollateralSecuritySection from './CollateralSecuritySection';
import EzRentChecksSection from './EzRentChecksSection';
import DTISection from './DTISection';
import RiskScoreSecuredSection from './RiskScoreSecuredSection';
import DecisionMemoSection from './DecisionMemoSection';

const verdictColors = {
    'Pass': 'bg-green-100 text-green-800',
    'Fail': 'bg-red-100 text-red-800',
    'Incomplete': 'bg-yellow-100 text-yellow-800',
    'Clean': 'bg-green-100 text-green-800',
    'Minor': 'bg-yellow-100 text-yellow-800',
    'Default': 'bg-red-100 text-red-800',
    'Level 1 — Clear': 'bg-green-100 text-green-800',
    'Level 2 — Escalate': 'bg-yellow-100 text-yellow-800',
    'Level 3 — Reject': 'bg-red-100 text-red-800',
    'Review': 'bg-yellow-100 text-yellow-800',
    'Proceed': 'bg-green-100 text-green-800',
    'Reject': 'bg-red-100 text-red-800',
};

const VerdictBadge = ({ verdict }) => {
    if (!verdict) return null;
    const color = verdictColors[verdict] || 'bg-gray-100 text-gray-700';
    return <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full ${color}`}>{verdict}</span>;
};

const SectionHeader = ({ label, verdict }) => (
    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-y border-gray-200 rounded-t-lg">
        <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">{label}</h3>
        {verdict && <VerdictBadge verdict={verdict} />}
    </div>
);

const CheckRow = ({ label, name, value, options, onChange }) => (
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 hover:bg-gray-50/40 transition-colors">
        <span className="text-sm text-gray-700 w-3/5">{label}</span>
        <select name={name} value={value || ''} onChange={onChange}
            className="w-2/5 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400">
            <option value="">—</option>
            {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
    </div>
);

const passFailOptions = ['Pass', 'Fail', 'N/A'];
const passFail = ['Pass', 'Fail'];
const passFlagOptions = ['Pass', 'Flag'];
const bureauOptions = ['Clean', 'Minor', 'Default'];

function hasPermission(permissions, slug) {
    if (!permissions) return false;
    return permissions.includes(slug) || permissions.includes('*:*');
}

function SemiSecuredEval({ loanId, loanType }) {
    const accessToken = useSelector(selectAccessToken);
    const permissions = useSelector(selectPermissions);
    const dispatch = useDispatch();

    const canView = hasPermission(permissions, 'evaluation:view');
    const canEdit = hasPermission(permissions, 'evaluation:edit');
    const readOnly = !canEdit;
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const isEzRent = (loanType || '').toLowerCase().includes('ezrent');

    const [masterInput, setMasterInput] = useState({});
    const [kyc, setKyc] = useState({});
    const [creditBureau, setCreditBureau] = useState({});
    const [fraud, setFraud] = useState({});
    const [collateralSecurity, setCollateralSecurity] = useState({});
    const [ezRentChecks, setEzRentChecks] = useState({});
    const [preDisbursement, setPreDisbursement] = useState({});
    const [creditMemo, setCreditMemo] = useState({});
    const [overallEligibility, setOverallEligibility] = useState('');

    const [dti, setDti] = useState({});
    const [riskScore, setRiskScore] = useState({});
    const [decisionMemo, setDecisionMemo] = useState({});
    const [dtiParams, setDtiParams] = useState({
        declaredDebt: 0, requestedAmount: 0, annualRate: 36, tenureMonths: 12, momoIncomeIncluded: false
    });
    const [computing, setComputing] = useState(false);

    const fetchEval = useCallback(async () => {
        try {
            const res = await fetchRequests(`/loans/staff/evaluation?loanId=${loanId}`, "GET", authStaff(accessToken), {}, {});
            if (res?.evaluation) {
                const e = res.evaluation;
                setMasterInput(e.masterInput || {});
                setKyc(e.kyc || {});
                setCreditBureau(e.creditBureau || {});
                setFraud(e.fraud || {});
                setCollateralSecurity(e.collateralSecurity || {});
                setEzRentChecks(e.ezRentChecks || {});
                setPreDisbursement(e.preDisbursement || {});
                setCreditMemo(e.creditMemo || {});
                setOverallEligibility(e.overallEligibility || '');
                setDti(e.dti || {});
                setRiskScore(e.riskScore || {});
                setDecisionMemo(e.decisionMemo || {});
                if (e.dti) {
                    setDtiParams(prev => ({ ...prev, declaredDebt: e.dti.declaredDebt || 0, momoIncomeIncluded: e.dti.momoIncomeIncluded || false }));
                }
                if (e.decisionMemo) {
                    setDtiParams(prev => ({ ...prev, requestedAmount: e.decisionMemo.requestedAmount || 0, annualRate: e.decisionMemo.annualRate || 36, tenureMonths: e.decisionMemo.tenure || 12 }));
                }
            } else if (res?.masterInput) {
                setMasterInput(res.masterInput);
            }
        } catch (err) { /* no existing evaluation */ }
        finally { setLoading(false); }
    }, [loanId, accessToken]);

    useEffect(() => { fetchEval(); }, [fetchEval]);

    const updateSection = (setter) => (e) => {
        const { name, value } = e.target;
        setter(prev => ({ ...prev, [name]: value }));
    };

    const updateCheckbox = (setter) => (e) => {
        const { name, checked } = e.target;
        setter(prev => ({ ...prev, [name]: checked }));
    };

    const handleMasterInputChange = (_section, field, value) => {
        setMasterInput(prev => ({ ...prev, [field]: value }));
    };

    const handleCollateralChange = (_section, field, value) => {
        setCollateralSecurity(prev => {
            const updated = { ...prev, [field]: value };
            if (field === 'pledgedFutureMonths' || field === 'avgMonthlyContribution' || field === 'currentBalance') {
                const avgContrib = field === 'avgMonthlyContribution' ? value : (updated.avgMonthlyContribution || 0);
                const pledgedMonths = field === 'pledgedFutureMonths' ? value : (updated.pledgedFutureMonths || 0);
                const currentBal = field === 'currentBalance' ? value : (updated.currentBalance || 0);
                const projectedFuture = Math.round(avgContrib * pledgedMonths * 100) / 100;
                updated.projectedFutureContributions = projectedFuture;
                updated.totalPledgedSecurity = Math.round((currentBal + projectedFuture) * 100) / 100;
                if (dtiParams.requestedAmount > 0 && updated.totalPledgedSecurity > 0) {
                    updated.scr = Math.round((updated.totalPledgedSecurity / dtiParams.requestedAmount) * 10000) / 10000;
                }
            }
            return updated;
        });
    };

    const handleEzRentChange = (_section, field, value) => {
        setEzRentChecks(prev => {
            const updated = { ...prev, [field]: value };
            if (field === 'annualNetIncome' || field === 'rentAdvance') {
                const income = field === 'annualNetIncome' ? value : (updated.annualNetIncome || 0);
                const rent = field === 'rentAdvance' ? value : (updated.rentAdvance || 0);
                if (income > 0) {
                    updated.rentToIncomeRatio = Math.round((rent / income) * 10000) / 10000;
                    updated.rentToIncomeVerdict = updated.rentToIncomeRatio <= 0.4 ? 'Pass' : 'Fail';
                }
            }
            return updated;
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                loanId, loanType, pensionState: 'semi-secured', masterInput, kyc, creditBureau, fraud,
                collateralSecurity, preDisbursement, creditMemo
            };
            if (isEzRent) payload.ezRentChecks = ezRentChecks;
            const res = await fetchRequests('/loans/staff/evaluation', 'POST', authStaff(accessToken), {}, payload);
            if (res?.err) {
                dispatch(show({ type: 'error', state: true, message: res.err }));
            } else if (res?.evaluation) {
                const e = res.evaluation;
                setMasterInput(e.masterInput || {});
                setKyc(e.kyc || {});
                setCreditBureau(e.creditBureau || {});
                setFraud(e.fraud || {});
                setCollateralSecurity(e.collateralSecurity || {});
                setEzRentChecks(e.ezRentChecks || {});
                setPreDisbursement(e.preDisbursement || {});
                setCreditMemo(e.creditMemo || {});
                setOverallEligibility(e.overallEligibility || '');
                setDti(e.dti || {});
                setRiskScore(e.riskScore || {});
                setDecisionMemo(e.decisionMemo || {});
                dispatch(show({ type: 'success', state: true, message: 'Evaluation saved' }));
            }
        } catch (err) {
            dispatch(show({ type: 'error', state: true, message: 'Failed to save evaluation' }));
        } finally { setSaving(false); }
    };

    const handleComputeDTI = async () => {
        setComputing(true);
        try {
            const res = await fetchRequests('/loans/staff/evaluation/compute-dti', 'POST', authStaff(accessToken), {}, {
                loanId,
                declaredDebt: Number(dtiParams.declaredDebt) || 0,
                requestedAmount: Number(dtiParams.requestedAmount) || 0,
                annualRate: Number(dtiParams.annualRate) || 36,
                tenureMonths: Number(dtiParams.tenureMonths) || 12,
                momoIncomeIncluded: dtiParams.momoIncomeIncluded
            });
            if (res?.err) {
                dispatch(show({ type: 'error', state: true, message: res.err }));
            } else {
                if (res?.dti) setDti(res.dti);
                if (res?.riskScore) setRiskScore(res.riskScore);
                if (res?.decisionMemo) setDecisionMemo(res.decisionMemo);
                dispatch(show({ type: 'success', state: true, message: 'DTI & risk score computed' }));
            }
        } catch (err) {
            dispatch(show({ type: 'error', state: true, message: 'Failed to compute DTI' }));
        } finally { setComputing(false); }
    };

    const updateDtiParam = (e) => {
        const { name, value, type, checked } = e.target;
        setDtiParams(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    if (!canView) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <p className="text-sm text-gray-500">You do not have permission to view the loan evaluation.</p>
            </div>
        );
    }

    if (loading) {
        return <div className="flex justify-center py-10"><Loader color="text-gray-500" size="w-5 h-5" /></div>;
    }

    const feeLabel = isEzRent ? '3% / 0.5%' : '3.5% / 0.65%';

    return (
        <fieldset disabled={readOnly} className="space-y-5 py-4 disabled:opacity-100">
            {readOnly && (
                <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-lg border border-amber-100">
                    <span className="text-xs font-medium text-amber-700 uppercase tracking-wide">View only — you do not have edit permission for this evaluation</span>
                </div>
            )}
            <div className="flex items-center justify-between gap-2 px-4 py-2 bg-blue-50 rounded-lg border border-blue-100">
                <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">
                    Template: Semi-Secured {isEzRent ? '(ezRent)' : '(FutureSecured)'} Loan Evaluation
                </span>
                <DownloadTemplateButton loanId={loanId} />
            </div>

            <MasterInputSection masterInput={masterInput} onChange={handleMasterInputChange} readOnly={readOnly} />

            {overallEligibility && (
                <div className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-gray-100 shadow-sm">
                    <span className="text-sm font-medium text-gray-700">Overall Eligibility:</span>
                    <VerdictBadge verdict={overallEligibility} />
                </div>
            )}

            {/* KYC */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader label="KYC & Employment Verification" verdict={kyc.kycVerdict} />
                <CheckRow label="Ghana Card sighted" name="ghanaCardSighted" value={kyc.ghanaCardSighted} options={passFailOptions} onChange={updateSection(setKyc)} />
                <CheckRow label="Ghana Card validated (NIA)" name="ghanaCardValidated" value={kyc.ghanaCardValidated} options={passFailOptions} onChange={updateSection(setKyc)} />
                <CheckRow label="Name consistency across documents" name="nameConsistency" value={kyc.nameConsistency} options={passFailOptions} onChange={updateSection(setKyc)} />
                <CheckRow label="GPS address verified" name="gpsAddressVerified" value={kyc.gpsAddressVerified} options={passFailOptions} onChange={updateSection(setKyc)} />
                <CheckRow label="Residence in supported region" name="residenceInSupportedRegion" value={kyc.residenceInSupportedRegion} options={passFailOptions} onChange={updateSection(setKyc)} />
                <CheckRow label="Supporting documents sighted" name="supportingDocSighted" value={kyc.supportingDocSighted} options={passFailOptions} onChange={updateSection(setKyc)} />
                <CheckRow label="Employment status check" name="employmentStatusCheck" value={kyc.employmentStatusCheck} options={passFailOptions} onChange={updateSection(setKyc)} />
                <CheckRow label="Tenure check (min. 12 months)" name="tenureCheck" value={kyc.tenureCheck} options={passFailOptions} onChange={updateSection(setKyc)} />
                <CheckRow label="Employer confirmation" name="employerConfirmation" value={kyc.employerConfirmation} options={passFailOptions} onChange={updateSection(setKyc)} />
                <div className="px-4 py-3">
                    <label className="block text-sm font-medium text-gray-600 mb-1">KYC Notes</label>
                    <textarea name="kycNotes" value={kyc.kycNotes || ''} onChange={updateSection(setKyc)} rows={2} className="w-full text-sm border border-gray-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                </div>
            </div>

            {/* Credit Bureau */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader label="Credit Bureau Review" verdict={creditBureau.bureauVerdict} />
                <CheckRow label="XDS delinquency status" name="xdsDelinquency" value={creditBureau.xdsDelinquency} options={bureauOptions} onChange={updateSection(setCreditBureau)} />
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-700 w-3/5">XDS report date</span>
                    <input type="date" name="xdsReportDate" value={creditBureau.xdsReportDate ? creditBureau.xdsReportDate.substring(0, 10) : ''} onChange={updateSection(setCreditBureau)} className="w-2/5 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                </div>
                <CheckRow label="D&B delinquency status" name="dnbDelinquency" value={creditBureau.dnbDelinquency} options={bureauOptions} onChange={updateSection(setCreditBureau)} />
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-700 w-3/5">D&B report date</span>
                    <input type="date" name="dnbReportDate" value={creditBureau.dnbReportDate ? creditBureau.dnbReportDate.substring(0, 10) : ''} onChange={updateSection(setCreditBureau)} className="w-2/5 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-700 w-3/5">Missed payments (count)</span>
                    <input type="number" name="missedPayments" value={creditBureau.missedPayments || 0} onChange={updateSection(setCreditBureau)} min={0} className="w-2/5 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                </div>
                {creditBureau.worstClassification && (
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                        <span className="text-sm text-gray-700">Worst classification</span>
                        <VerdictBadge verdict={creditBureau.worstClassification} />
                    </div>
                )}
                <div className="px-4 py-3">
                    <label className="block text-sm font-medium text-gray-600 mb-1">Bureau Notes</label>
                    <textarea name="bureauNotes" value={creditBureau.bureauNotes || ''} onChange={updateSection(setCreditBureau)} rows={2} className="w-full text-sm border border-gray-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                </div>
            </div>

            {/* Fraud & Compliance */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader label="Fraud & Compliance" verdict={fraud.fraudVerdict} />
                <CheckRow label="Document authenticity (Ghana Card, payslip, bank stmt, pension stmt)" name="documentAuthenticity" value={fraud.documentAuthenticity} options={passFlagOptions} onChange={updateSection(setFraud)} />
                <CheckRow label="Name consistency across all docs" name="nameConsistencyAllDocs" value={fraud.nameConsistencyAllDocs} options={passFlagOptions} onChange={updateSection(setFraud)} />
                <CheckRow label="Salary matches payslip (≤10% variance)" name="salaryMatchesPayslip" value={fraud.salaryMatchesPayslip} options={passFlagOptions} onChange={updateSection(setFraud)} />
                <CheckRow label="Pension contribution pattern consistent with employment" name="pensionContributionPattern" value={fraud.pensionContributionPattern} options={passFlagOptions} onChange={updateSection(setFraud)} />
                <CheckRow label="Bank statement not altered" name="bankStatementNotAltered" value={fraud.bankStatementNotAltered} options={passFlagOptions} onChange={updateSection(setFraud)} />
                <CheckRow label="Guarantor not flagged" name="guarantorNotFlagged" value={fraud.guarantorNotFlagged} options={passFlagOptions} onChange={updateSection(setFraud)} />
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Contribution-as-Income Check</span>
                    <div className="mt-2">
                        <CheckRow label="Contribution within expected range (5%–15% of income)?" name="contributionAsIncomeInRange" value={fraud.contributionAsIncomeInRange} options={['Yes', 'No']} onChange={updateSection(setFraud)} />
                    </div>
                </div>
                <div className="px-4 py-3 border-b border-gray-100 space-y-2">
                    <span className="text-sm font-medium text-gray-600">Consent</span>
                    <div className="flex items-center gap-6">
                        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                            <input type="checkbox" name="consentSMS" checked={fraud.consentSMS || false} onChange={updateCheckbox(setFraud)} className="h-4 w-4 accent-blue-600 rounded" /> SMS
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                            <input type="checkbox" name="consentWhatsApp" checked={fraud.consentWhatsApp || false} onChange={updateCheckbox(setFraud)} className="h-4 w-4 accent-blue-600 rounded" /> WhatsApp
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                            <input type="checkbox" name="consentDataSharing" checked={fraud.consentDataSharing || false} onChange={updateCheckbox(setFraud)} className="h-4 w-4 accent-blue-600 rounded" /> Data Sharing (Pension Trustee)
                        </label>
                    </div>
                </div>
                <div className="px-4 py-3">
                    <label className="block text-sm font-medium text-gray-600 mb-1">Fraud Notes</label>
                    <textarea name="fraudNotes" value={fraud.fraudNotes || ''} onChange={updateSection(setFraud)} rows={2} className="w-full text-sm border border-gray-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                </div>
            </div>

            {/* Collateral & Security */}
            <CollateralSecuritySection collateralSecurity={collateralSecurity} onChange={handleCollateralChange} />

            {/* ezRent Checks (only for ezRent loans) */}
            {isEzRent && <EzRentChecksSection ezRentChecks={ezRentChecks} onChange={handleEzRentChange} />}

            {/* Pre-Disbursement */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader label="Pre-Disbursement Controls" />
                <CheckRow label="Customer not flagged" name="customerNotFlagged" value={preDisbursement.customerNotFlagged} options={passFail} onChange={updateSection(setPreDisbursement)} />
                <CheckRow label="All documents authenticated" name="allDocsAuthenticated" value={preDisbursement.allDocsAuthenticated} options={passFail} onChange={updateSection(setPreDisbursement)} />
                <CheckRow label="Ghana Card NIA validated" name="ghanaCardNIAValidated" value={preDisbursement.ghanaCardNIAValidated} options={passFail} onChange={updateSection(setPreDisbursement)} />
                <CheckRow label="GPS verified online" name="gpsVerifiedOnline" value={preDisbursement.gpsVerifiedOnline} options={passFail} onChange={updateSection(setPreDisbursement)} />
                <CheckRow label="Standing order established" name="standingOrderEstablished" value={preDisbursement.standingOrderEstablished} options={passFail} onChange={updateSection(setPreDisbursement)} />
                <CheckRow label="Collateral pledge confirmed by trustee" name="guarantorFullyVerified" value={preDisbursement.guarantorFullyVerified} options={passFail} onChange={updateSection(setPreDisbursement)} />
                <CheckRow label="Loan purpose aligns" name="loanPurposeAligns" value={preDisbursement.loanPurposeAligns} options={passFail} onChange={updateSection(setPreDisbursement)} />
                <CheckRow label="Amount within limits" name="amountWithinLimits" value={preDisbursement.amountWithinLimits} options={passFail} onChange={updateSection(setPreDisbursement)} />
                <CheckRow label="Disbursement account verified" name="disbursementAccountVerified" value={preDisbursement.disbursementAccountVerified} options={passFail} onChange={updateSection(setPreDisbursement)} />
                <CheckRow label="Final DTI confirmed" name="finalDTIConfirmed" value={preDisbursement.finalDTIConfirmed} options={passFail} onChange={updateSection(setPreDisbursement)} />
                <div className="px-4 py-3">
                    <label className="block text-sm font-medium text-gray-600 mb-1">Pre-Disbursement Notes</label>
                    <textarea name="preDisbursementNotes" value={preDisbursement.preDisbursementNotes || ''} onChange={updateSection(setPreDisbursement)} rows={2} className="w-full text-sm border border-gray-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                </div>
            </div>

            {/* DTI Inputs */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-y border-gray-200 rounded-t-lg">
                    <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">DTI & Risk Score Inputs</h3>
                    <span className="text-xs text-gray-400">Flat repayment · 60% DTI cap · {feeLabel} fees</span>
                </div>
                <div className="grid grid-cols-2 gap-4 px-4 py-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Declared existing debt (GHS/mo)</label>
                        <input type="number" name="declaredDebt" value={dtiParams.declaredDebt} onChange={updateDtiParam} min={0} className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Requested loan amount (GHS)</label>
                        <input type="number" name="requestedAmount" value={dtiParams.requestedAmount} onChange={updateDtiParam} min={0} className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Annual rate (%)</label>
                        <input type="number" name="annualRate" value={dtiParams.annualRate} onChange={updateDtiParam} min={0} step={0.5} className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Tenure (months)</label>
                        <input type="number" name="tenureMonths" value={dtiParams.tenureMonths} onChange={updateDtiParam} min={1} max={60} className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                    </div>
                </div>
                <div className="flex items-center gap-4 px-4 pb-4">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" name="momoIncomeIncluded" checked={dtiParams.momoIncomeIncluded} onChange={updateDtiParam} className="h-4 w-4 accent-blue-600 rounded" />
                        Include MoMo income in DTI calculation
                    </label>
                    <button type="button" onClick={handleComputeDTI} disabled={computing}
                        className="ml-auto inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-[#1e3a6e] rounded-lg hover:bg-[#162d56] transition-colors disabled:opacity-50">
                        {computing && <Loader color="text-white" size="w-4 h-4" />}
                        {computing ? 'Computing...' : 'Compute DTI & Score'}
                    </button>
                </div>
            </div>

            <DTISection dti={dti} />
            <RiskScoreSecuredSection riskScore={riskScore} />
            <DecisionMemoSection decisionMemo={decisionMemo} evaluation={{ kyc, creditBureau, fraud, dti, collateralSecurity }} />

            {/* Credit Memo */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader label="Credit Memo" />
                <div className="px-4 py-3 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Key Risks</label>
                        <textarea name="keyRisks" value={creditMemo.keyRisks || ''} onChange={updateSection(setCreditMemo)} rows={3} className="w-full text-sm border border-gray-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" placeholder="Summarize the main risks identified..." />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Mitigating Factors</label>
                        <textarea name="mitigatingFactors" value={creditMemo.mitigatingFactors || ''} onChange={updateSection(setCreditMemo)} rows={3} className="w-full text-sm border border-gray-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" placeholder="What offsets the risks identified above..." />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Conditions of Approval</label>
                        <textarea name="conditionsOfApproval" value={creditMemo.conditionsOfApproval || ''} onChange={updateSection(setCreditMemo)} rows={3} className="w-full text-sm border border-gray-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" placeholder="Any conditions that must be met before disbursement..." />
                    </div>
                </div>
            </div>

            {/* Save */}
            {canEdit && (
                <div className="flex items-center gap-3">
                    <button type="button" onClick={handleSave} disabled={saving}
                        className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-[#1e3a6e] rounded-lg hover:bg-[#162d56] transition-colors disabled:opacity-50">
                        {saving && <Loader color="text-white" size="w-4 h-4" />}
                        {saving ? 'Saving...' : 'Save Evaluation'}
                    </button>
                    {overallEligibility && (
                        <span className="text-sm text-gray-500">Overall: <VerdictBadge verdict={overallEligibility} /></span>
                    )}
                </div>
            )}
        </fieldset>
    );
}

export default SemiSecuredEval;
